// The livery of the Fleet Designer (R1252, ui-map-interface.md §6.1.6).
//
// One pick per account out of the twenty rows of `LIVERIES`, or NONE (wire
// 0). This leaf holds the geometry of every option, point for point from the
// card that pitched it, and the one bake that paints an option on a hull
// master. The atlas (`fast/gfx/atlas.ts`) and the hub preview
// (`ui/hub/panels/liverypreview.ts`) both call `bakeLivery`, so the card and
// the map cannot draw two looks.
//
//   - the patterns: mockup round 060, `archive/mockups/060-fleet-livery.html`
//   - the decals:   mockup round 061, `archive/mockups/061-fleet-decal.html`
//
// **Hull space.** The nose points to +x, and a figure is a map px at 1x.
//
// **Every option is clipped to the silhouette** of the hull the bake is
// handed, which is the hull of the ship icon set in use (R835). A set with
// no wing therefore shows no wing band, and the extras of a set carry the
// pattern too. The tips read the half span of that silhouette and not a
// figure of the default set, so every set gets a sane result.
//
// **A livery is a pattern and not a hue** (themes.md §10). The bake paints
// white or cuts alpha; the quad carries the faction ink.

import { LIVERIES, LIVERY_INK, type LiveryCode } from '../gen/presentation';
import { BoxProbe, Canvas2DMarks, lift, type HullPath } from './marks';

/** One row of the option list. */
export type LiveryRow = (typeof LIVERIES)[number];

const BY_WIRE = new Map<number, LiveryRow>(LIVERIES.map((r) => [r.wire, r]));
const BY_CODE = new Map<string, LiveryRow>(LIVERIES.map((r) => [r.code, r]));

/** The row a wire number names, or null for NONE and for a number this
 *  build does not know. */
export function liveryOfWire(wire: number): LiveryRow | null {
  return wire === 0 ? null : BY_WIRE.get(wire) ?? null;
}

/** The row a code names, or null. */
export function liveryOfCode(code: string): LiveryRow | null {
  return BY_CODE.get(code) ?? null;
}

/** The hubWardroom string key of an option's name. */
export function liveryNameKey(code: LiveryCode): `livery_name_${LiveryCode}` {
  return `livery_name_${code}`;
}

/** What an option needs to know of the hull it lies on. */
export interface LiveryHull {
  /** A support hull: the one needle of R835 and its extras. */
  support: boolean;
  /** The half span of the silhouette, in px: the largest |y| it reaches. */
  span: number;
}

/**
 * The half span of a hull path: the largest |y| it reaches, with no stroke.
 * A circle and an arc count their whole radius, which can only over-state
 * the span of a hull and never under-state it.
 */
export function hullSpan(path: HullPath): number {
  const probe = new BoxProbe();
  path(probe);
  return Math.max(0, -probe.box.y0, probe.box.y1);
}

/** True for the five cockpit shapes, which replace the cockpit dot. */
export function isCockpit(code: LiveryCode): boolean {
  return code.startsWith('cockpit_');
}

type Ctx = CanvasRenderingContext2D;

/** An axis-aligned rectangle from two corners (the mockups' `rect`). */
function rect(c: Ctx, x0: number, y0: number, x1: number, y1: number): void {
  c.fillRect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
}

/** An open polyline of width `w` (the mockups' `stroke`). */
function polyline(c: Ctx, pts: readonly (readonly [number, number])[], w: number): void {
  c.beginPath();
  pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  c.lineWidth = w;
  c.lineCap = 'butt';
  c.lineJoin = 'miter';
  c.stroke();
}

/** A filled disc. */
function dot(c: Ctx, x: number, y: number, r: number): void {
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.fill();
}

// The cockpit dot of the dart stands at (4, 0) with r 2.4 (`atlas.ts
// hullSpec`); card 061-01 puts the shape there at the dot's own size, and at
// r 1.4 on a support hull.
const COCKPIT_X = 4;
const COCKPIT_R = 2.4;
const COCKPIT_R_SUPPORT = 1.4;
// The width of a cockpit shape on a hollow hull, where it is a stroke.
const COCKPIT_HOLLOW_W = 0.9;

/**
 * Paints one option in hull space. The fill and the stroke style are set by
 * the caller, and the caller clips to the hull. `hollow` says the hull is the
 * hollow squadmate master, which strokes a cockpit shape in place of a fill.
 */
export function paintLivery(c: Ctx, code: LiveryCode, h: LiveryHull, hollow: boolean): void {
  const sup = h.support;
  switch (code) {
    // 060-01: one stripe along the axis, 2.2 px, 1.2 px on the needle.
    case 'centre_stripe': {
      const w = sup ? 0.6 : 1.1;
      rect(c, -10, -w, 15, w);
      return;
    }
    // 060-02: two stripes at ±2.6 px, 1.4 px each; ±1.0 px on the needle.
    case 'twin_stripes': {
      const y = sup ? 1.0 : 2.6, w = sup ? 0.35 : 0.7;
      rect(c, -10, y - w, 15, y + w);
      rect(c, -10, -y - w, 15, -y + w);
      return;
    }
    // 060-03: one band across the hull behind the nose.
    case 'bow_band':
      rect(c, sup ? 7 : 6, -12, sup ? 9.5 : 9, 12);
      return;
    // 060-04: one band across the tail.
    case 'stern_band':
      rect(c, -7, -12, -4, 12);
      return;
    // 060-06: the silhouette again at 55 % about (2.5, 0). It is painted by
    // `bakeLivery`, which holds the path; nothing to do here.
    case 'inner_silhouette':
      return;
    // 060-07: one chevron amidships, point forward at x 5.
    case 'chevron': {
      const [s, w] = sup ? [3, 1.2] : [5, 1.8];
      polyline(c, [[-1, -s], [5, 0], [-1, s]], w);
      return;
    }
    // 060-08: three bars at 35 degrees, 2.6 px wide at a 5 px pitch.
    case 'dazzle':
      c.save();
      c.rotate(-0.61);
      for (let k = -1; k <= 1; k++) rect(c, k * 5 - 1.3, -16, k * 5 + 1.3, 16);
      c.restore();
      return;
    // 060-09: a row of dots along the axis.
    case 'rivets': {
      const r = sup ? 0.6 : 0.9;
      const xs = sup ? [-3, 0, 3, 6, 9] : [-7, -3.5, 0, 8, 11];
      for (const x of xs) dot(c, x, 0, r);
      return;
    }
    // 060-10: hairlines at 45 degrees, 0.7 px at a 2.4 px pitch.
    case 'hatching':
      c.save();
      c.rotate(Math.PI / 4);
      for (let k = -8; k <= 8; k++) rect(c, k * 2.4 - 0.35, -20, k * 2.4 + 0.35, 20);
      c.restore();
      return;
    // 060-12: the nose and the outer band of each wing. The card's wing band
    // starts 3.5 px inside the span (1.8 px on a support hull, where the
    // Tender's ring is the span); this reads the span of the set in use.
    case 'tips': {
      const edge = h.span + 1;
      const inner = h.span - (sup ? 1.8 : 3.5);
      if (sup) rect(c, 9.5, -3, 14, 3);
      else rect(c, 10.5, -6, 15, 6);
      rect(c, -20, inner, 20, edge);
      rect(c, -20, -inner, 20, -edge);
      return;
    }
    // 061-01: the cockpit dot becomes a shape.
    case 'cockpit_diamond':
    case 'cockpit_square':
    case 'cockpit_triangle':
    case 'cockpit_ring':
    case 'cockpit_bar': {
      const r = sup ? COCKPIT_R_SUPPORT : COCKPIT_R, x = COCKPIT_X;
      c.beginPath();
      if (code === 'cockpit_diamond') {
        c.moveTo(x + r, 0); c.lineTo(x, r); c.lineTo(x - r, 0); c.lineTo(x, -r); c.closePath();
      } else if (code === 'cockpit_square') {
        c.rect(x - r * 0.85, -r * 0.85, r * 1.7, r * 1.7);
      } else if (code === 'cockpit_triangle') {
        c.moveTo(x + r, 0); c.lineTo(x - r * 0.8, r * 0.95); c.lineTo(x - r * 0.8, -r * 0.95); c.closePath();
      } else if (code === 'cockpit_ring') {
        c.arc(x, 0, r, 0, Math.PI * 2);
        c.moveTo(x + r * 0.45, 0);
        c.arc(x, 0, r * 0.45, 0, Math.PI * 2, true);
      } else {
        c.rect(x - r * 0.45, -r, r * 0.9, r * 2);
      }
      if (hollow) { c.lineWidth = COCKPIT_HOLLOW_W; c.stroke(); } else c.fill();
      return;
    }
    // 061-02: a hollow plate amidships with one to three tally strokes.
    case 'tally_one':
    case 'tally_two':
    case 'tally_three': {
      const k = sup ? 0.6 : 1;
      const n = code === 'tally_one' ? 1 : code === 'tally_two' ? 2 : 3;
      c.lineWidth = 0.7 * k;
      c.strokeRect(-5 * k, -1.5 * k, 6 * k, 3 * k);
      for (let i = 0; i < n; i++) {
        const x = -2 * k + (i - (n - 1) / 2) * 1.4 * k;
        c.beginPath(); c.moveTo(x, -0.9 * k); c.lineTo(x, 0.9 * k); c.stroke();
      }
      return;
    }
    // 061-05: a hollow ring amidships at (-1, 0), outer r 2.1, inner r 1.3.
    case 'deck_ring': {
      const k = sup ? 0.65 : 1;
      c.beginPath();
      c.arc(-1, 0, 2.1 * k, 0, Math.PI * 2);
      c.moveTo(-1 + 1.3 * k, 0);
      c.arc(-1, 0, 1.3 * k, 0, Math.PI * 2, true);
      c.fill();
      return;
    }
    // 061-11: one band across each wing at |y| 6.2 to 7.6; the Tender takes
    // it on the lifebuoy at |y| 3.8 to 4.8. A hull with no wing takes none,
    // because the clip leaves nothing.
    case 'wing_band': {
      const a = sup ? 3.8 : 6.2, b = sup ? 4.8 : 7.6;
      rect(c, -20, a, 20, b);
      rect(c, -20, -a, 20, -b);
      return;
    }
  }
}

/** The three hull masters, and the mask the overlay quad draws. */
export type LiveryMaster = 'filled' | 'hollow' | 'mask';

/** What `bakeLivery` draws with. */
export interface LiveryBake {
  path: HullPath;
  hull: LiveryHull;
  row: LiveryRow;
  master: LiveryMaster;
  /** The dart's cockpit dot, which a filled master carries (`hullSpec`). */
  cockpitDot: boolean;
  /** The stroke width of the hollow master (FLEET_HOLLOW_STROKE_PX). */
  hollowStrokePx: number;
}

/** The inner silhouette of 060-06: the hull again at 55 % about (2.5, 0). */
const INNER_K = 0.55;
const INNER_X = 2.5;

/** Lays down the hull path as the current path of `c`. */
function hullPath(c: Ctx, path: HullPath): void {
  c.beginPath();
  path(new Canvas2DMarks(c));
}

/** Paints the option, with the styles set, under the current clip. */
function paintRow(c: Ctx, b: LiveryBake): void {
  if (b.row.code === 'inner_silhouette') {
    c.save();
    c.translate(INNER_X, 0);
    c.scale(INNER_K, INNER_K);
    c.translate(-INNER_X, 0);
    hullPath(c, b.path);
    c.fill();
    c.restore();
    return;
  }
  paintLivery(c, b.row.code, b.hull, b.master === 'hollow');
}

/**
 * Bakes one liveried master, WHITE, into `c` in hull space.
 *
 *   filled  the white master; a dark step cuts the alpha of the option by
 *           `LIVERY_INK.darkCut`, so the water shows through. A light step
 *           bakes the plain master: the mask quad adds its ink.
 *   hollow  the option at the hollow alpha of its family, then the stroke.
 *   mask    the option alone, opaque, for the overlay quad of the light
 *           step and of the own white hull.
 */
export function bakeLivery(c: Ctx, b: LiveryBake): void {
  const m = new Canvas2DMarks(c);
  if (b.master === 'filled') {
    c.beginPath();
    b.path(m);
    m.fill({ color: 0xffffff });
    if (b.cockpitDot && !isCockpit(b.row.code)) m.circle(COCKPIT_X, 0, COCKPIT_R).fill({ color: 0xffffff, alpha: 0.9 });
    if (b.row.step !== 'dark') return;
    c.save();
    hullPath(c, b.path);
    c.clip();
    c.globalCompositeOperation = 'destination-out';
    c.fillStyle = c.strokeStyle = `rgba(0,0,0,${LIVERY_INK.darkCut})`;
    paintRow(c, b);
    c.restore();
    return;
  }
  c.save();
  hullPath(c, b.path);
  c.clip();
  const a = b.master === 'mask' ? 1
    : b.row.family === 'decal' ? LIVERY_INK.hollowDecalAlpha : LIVERY_INK.hollowPatternAlpha;
  c.fillStyle = c.strokeStyle = `rgba(255,255,255,${a})`;
  paintRow(c, b);
  c.restore();
  if (b.master === 'hollow') {
    c.beginPath();
    b.path(m);
    m.stroke({ width: b.hollowStrokePx, color: 0xffffff, join: 'round' });
  }
}

/**
 * How the fleet layer draws one liveried hull (§6.1.6): which base cell, and
 * whether the mask quad goes over it in which ink and alpha.
 *
 * `ownWhite` is your own hull under the white core (R185); `color` is the ink
 * the hull quad carries today.
 */
export interface LiveryPlan {
  /** Bake the liveried cell (true) or draw the plain master (false). */
  liveried: boolean;
  /** Draw the mask quad over the hull. */
  mask: boolean;
  /** The ink and the alpha share of the mask quad. */
  maskColor: number;
  maskAlpha: number;
}

/** The one plan `liveryPlan` writes into. */
const PLAN: LiveryPlan = { liveried: false, mask: false, maskColor: 0, maskAlpha: 0 };

/** Writes the four fields of the plan and hands it back. */
function plan(liveried: boolean, mask: boolean, maskColor: number, maskAlpha: number): LiveryPlan {
  PLAN.liveried = liveried;
  PLAN.mask = mask;
  PLAN.maskColor = maskColor;
  PLAN.maskAlpha = maskAlpha;
  return PLAN;
}

/**
 * The plan for one hull. `faction` is the faction ink and `color` the ink
 * the hull quad carries, both as 0xRRGGBB.
 *
 * The fleet layer asks once a liveried hull a frame, so the answer is one
 * object that every call writes again (§10): read it before the next call.
 */
export function liveryPlan(
  row: LiveryRow, hollow: boolean, ownWhite: boolean, color: number, faction: number,
): LiveryPlan {
  if (hollow) return plan(true, false, 0, 0);
  if (ownWhite) {
    return plan(false, true, faction,
      row.step === 'dark' ? LIVERY_INK.ownDarkAlpha : LIVERY_INK.ownLightAlpha);
  }
  if (row.step === 'dark') return plan(true, false, 0, 0);
  return plan(false, true, lift(color, LIVERY_INK.lightLift), 1);
}
